import { Search } from 'lucide-react';

import { IconButton } from 'features/entry-form-v2/component/IconButton';
import { useState } from 'react';
import { Dialog } from 'features/entry-form-v2/component/Dialog';
import { LanguageType } from 'types';
import { useLanguage } from 'features/hooks/useLanguage';
import {
	SearchRecordsFilter,
	SearchRecordsResponse,
	useSearchRecordsMutation
} from '../data/useSearchRecordsMutation';
import { Input } from 'features/entry-form-v2/component/Input';
import { useForm } from 'react-hook-form';
import { Button } from 'features/components/Button';
import { Link, useParams } from 'react-router-dom';
import { ROUTE_MAP } from 'features/entry-form-v2/utils/routeMap';
import clsx from 'clsx';
import { zodResolver } from '@hookform/resolvers/zod';
import { CodelistIcon, CodelistIcons } from '../icons/codelist-icons';
import z from 'zod/v4';
import { DateTime } from 'luxon';
import { useLocalStorage } from 'features/hooks/useLocalStorage';
import { CODELIST_SIDEBAR_DICTIONARY } from '../sidebar/Dictionary';

export const SearchCodelistModal = () => {
	const language = useLanguage();

	const projectId = useProjectId();

	const [showModal, setShowModal] = useState(false);

	const translation = Dictionary[language];

	const lookupMutation = useSearchRecordsMutation({ projectId: projectId ?? '' });

	const [visibleKeys, setVisibleKeys] = useLocalStorage<SearchCodelistRecordKey[]>(
		'codelist-visible-keys',
		['ssn']
	);

	const form = useForm({
		resolver: zodResolver(searchCodelistRecordSchema)
	});

	return (
		<>
			<IconButton
				className="relative p-[8px] flex gap-2 items-center shrink-0"
				onClick={() => setShowModal(true)}
			>
				<Search />
				<p className="text-base">{Dictionary[language].openModalButton}</p>
			</IconButton>

			<Dialog
				open={showModal}
				onClose={() => setShowModal(false)}
				title={Dictionary[language].title}
			>
				<div className="w-[600px]">
					<div className="flex gap-2 overflow-x-auto pb-4 mb-4">
						{searchCodelistRecordKeys.map(key => {
							const isVisible = visibleKeys.includes(key);

							return (
								<button
									key={key}
									className={clsx(
										'border border-gray-200 rounded-md p-2 hover:bg-gray-100 shrink-0',
										isVisible && 'bg-gray-200'
									)}
									onClick={() =>
										setVisibleKeys(prev => {
											if (prev.includes(key)) {
												form.resetField(key);
												return prev.filter(k => k !== key);
											}
											return [...prev, key];
										})
									}
								>
									{translation.modal.keys[key]}
								</button>
							);
						})}
					</div>

					<form
						className="flex flex-col gap-4"
						onSubmit={form.handleSubmit(data => {
							const filters: SearchRecordsFilter[] = [];

							for (const key of visibleKeys) {
								const value = data[key];
								if (value) {
									filters.push({ key, value });
								}
							}

							lookupMutation.mutate({
								filters
							});
						})}
					>
						{visibleKeys.length === 0 && (
							<p className="text-base text-gray-900 mt-4 text-center">
								{translation.modal.noKeysSelected}
							</p>
						)}

						{visibleKeys.includes('ssn') && (
							<Input
								label={translation.modal.keys.ssn}
								id="ssn"
								{...form.register('ssn')}
							/>
						)}

						{visibleKeys.includes('firstName') && (
							<Input
								label={translation.modal.keys.firstName}
								id="firstName"
								{...form.register('firstName')}
							/>
						)}

						{visibleKeys.includes('lastName') && (
							<Input
								label={translation.modal.keys.lastName}
								id="lastName"
								{...form.register('lastName')}
							/>
						)}

						{visibleKeys.includes('dateOfBirth') && (
							<Input
								label={translation.modal.keys.dateOfBirth}
								id="dateOfBirth"
								type="date"
								{...form.register('dateOfBirth')}
							/>
						)}

						{visibleKeys.includes('mobilePhone') && (
							<Input
								label={translation.modal.keys.mobilePhone}
								id="mobilePhone"
								{...form.register('mobilePhone')}
							/>
						)}

						{visibleKeys.includes('email') && (
							<Input
								label={translation.modal.keys.email}
								id="email"
								{...form.register('email')}
							/>
						)}

						{visibleKeys.includes('address') && (
							<Input
								label={translation.modal.keys.address}
								id="address"
								{...form.register('address')}
							/>
						)}

						{visibleKeys.includes('postalCode') && (
							<Input
								label={translation.modal.keys.postalCode}
								id="postalCode"
								{...form.register('postalCode')}
							/>
						)}

						{visibleKeys.includes('postalPlace') && (
							<Input
								label={translation.modal.keys.postalPlace}
								id="postalPlace"
								{...form.register('postalPlace')}
							/>
						)}

						{visibleKeys.includes('country') && (
							<Input
								label={translation.modal.keys.country}
								id="country"
								{...form.register('country')}
							/>
						)}

						{visibleKeys.includes('customId') && (
							<Input
								label={translation.modal.keys.customId}
								id="customId"
								{...form.register('customId')}
							/>
						)}

						<div className="flex gap-2 justify-end">
							<Button
								variant="secondary"
								type="button"
								onClick={() => setShowModal(false)}
								text={translation.modal.cancelButton}
							/>
							<Button
								loading={lookupMutation.isLoading}
								text={translation.modal.searchButton}
							/>
						</div>
					</form>

					{lookupMutation.isSuccess && (
						<div className="flex flex-col gap-2">
							{lookupMutation.data.records?.length === 0 && (
								<p className="text-xl italic p-4 text-gray-900">
									{translation.modal.noMatchingEntries}
								</p>
							)}

							{lookupMutation.data.records?.length > 0 && (
								<div className="flex flex-col gap-2">
									<p className="text-sm text-gray-500">
										{translation.modal.xMatchingEntries(
											lookupMutation.data.records.length
										)}
									</p>

									<ul className="flex gap-2">
										{lookupMutation.data.records?.map(record => (
											<li key={record.originalEntryId}>
												<Link
													to={ROUTE_MAP.projects.byId.dataset.update.createPath(
														{
															projectId,
															entryId: record.originalEntryId,
															formId: null
														}
													)}
												>
													<EntryCard record={record} />
												</Link>
											</li>
										))}
									</ul>
								</div>
							)}
						</div>
					)}
				</div>
			</Dialog>
		</>
	);
};

const EntryCard = ({ record }: { record: SearchRecordsResponse['records'][number] }) => {
	const displayName =
		record.firstName || record.lastName
			? `${record.firstName} ${record.lastName}`
			: 'No name specified';
	return (
		<div className="flex flex-col gap-2 border border-gray-200 rounded-md p-2 hover:bg-gray-100">
			<RecordDisplayItem Icon={CodelistIcons.ssn} value={record.ssn} />

			<RecordDisplayItem Icon={CodelistIcons.firstName} value={displayName} />

			{record.dateOfBirth && (
				<RecordDisplayItem
					Icon={CodelistIcons.dateOfBirth}
					value={
						record.dateOfBirth
							? DateTime.fromJSDate(new Date(record.dateOfBirth)).toLocaleString(
									DateTime.DATE_MED
							  )
							: undefined
					}
				/>
			)}
		</div>
	);
};

const RecordDisplayItem = ({ Icon, value }: { Icon: CodelistIcon; value?: string }) => {
	if (!value) return null;

	return (
		<p className="flex items-center gap-2 text-sm">
			<Icon className="size-4" />
			{value}
		</p>
	);
};

const useProjectId = () => {
	const { id: projectId } = useParams();

	if (!projectId) {
		throw new Error('useProjectId must be used within a route with :id param in path');
	}

	return projectId ?? '';
};

const Dictionary = {
	[LanguageType.English]: {
		title: 'Search in personal data',

		openModalButton: 'Search personal data',

		modal: {
			cancelButton: 'Cancel',

			searchButton: 'Search',

			noKeysSelected: 'Select at least one field to search',

			keys: {
				ssn: CODELIST_SIDEBAR_DICTIONARY[LanguageType.English].ssn.label,
				firstName: CODELIST_SIDEBAR_DICTIONARY[LanguageType.English].firstName.label,
				lastName: CODELIST_SIDEBAR_DICTIONARY[LanguageType.English].lastName.label,
				dateOfBirth: CODELIST_SIDEBAR_DICTIONARY[LanguageType.English].dateOfBirth.label,
				mobilePhone: CODELIST_SIDEBAR_DICTIONARY[LanguageType.English].mobilePhone.label,
				email: CODELIST_SIDEBAR_DICTIONARY[LanguageType.English].email.label,
				address: CODELIST_SIDEBAR_DICTIONARY[LanguageType.English].address.label,
				postalCode: CODELIST_SIDEBAR_DICTIONARY[LanguageType.English].postalCode.label,
				postalPlace: CODELIST_SIDEBAR_DICTIONARY[LanguageType.English].postalPlace.label,
				country: CODELIST_SIDEBAR_DICTIONARY[LanguageType.English].country.label,
				customId: CODELIST_SIDEBAR_DICTIONARY[LanguageType.English].customId.label
			},

			noMatchingEntries: 'No matching entries found',

			xMatchingEntries: (count: number) =>
				`${count} matching ${count === 1 ? 'entry' : 'entries'} found`
		}
	},
	[LanguageType.Norwegian]: {
		title: 'Søk i personopplysninger',

		openModalButton: 'Søk i personopplysninger',

		modal: {
			cancelButton: 'Avbryt',
			searchButton: 'Søk',

			noKeysSelected: 'Velg minst ett felt for å søke',

			keys: {
				ssn: CODELIST_SIDEBAR_DICTIONARY[LanguageType.Norwegian].ssn.label,
				firstName: CODELIST_SIDEBAR_DICTIONARY[LanguageType.Norwegian].firstName.label,
				lastName: CODELIST_SIDEBAR_DICTIONARY[LanguageType.Norwegian].lastName.label,
				dateOfBirth: CODELIST_SIDEBAR_DICTIONARY[LanguageType.Norwegian].dateOfBirth.label,
				mobilePhone: CODELIST_SIDEBAR_DICTIONARY[LanguageType.Norwegian].mobilePhone.label,
				email: CODELIST_SIDEBAR_DICTIONARY[LanguageType.Norwegian].email.label,
				address: CODELIST_SIDEBAR_DICTIONARY[LanguageType.Norwegian].address.label,
				postalCode: CODELIST_SIDEBAR_DICTIONARY[LanguageType.Norwegian].postalCode.label,
				postalPlace: CODELIST_SIDEBAR_DICTIONARY[LanguageType.Norwegian].postalPlace.label,
				country: CODELIST_SIDEBAR_DICTIONARY[LanguageType.Norwegian].country.label,
				customId: CODELIST_SIDEBAR_DICTIONARY[LanguageType.Norwegian].customId.label
			},

			noMatchingEntries: 'Ingen treff funnet',
			xMatchingEntries: (count: number) => `${count} treff funnet`
		}
	}
} as const;

const searchCodelistRecordSchema = z.object({
	ssn: z
		.string()
		.transform(val => (val === '' ? undefined : val))
		.optional(),
	firstName: z
		.string()
		.transform(val => (val === '' ? undefined : val))
		.optional(),
	lastName: z
		.string()
		.transform(val => (val === '' ? undefined : val))
		.optional(),
	dateOfBirth: z
		.string()
		.transform(val => (val === '' ? undefined : val))
		.optional(),
	mobilePhone: z
		.string()
		.transform(val => (val === '' ? undefined : val))
		.optional(),
	email: z
		.string()
		.transform(val => (val === '' ? undefined : val))
		.optional(),
	address: z
		.string()
		.transform(val => (val === '' ? undefined : val))
		.optional(),
	postalCode: z
		.string()
		.transform(val => (val === '' ? undefined : val))
		.optional(),
	postalPlace: z
		.string()
		.transform(val => (val === '' ? undefined : val))
		.optional(),
	country: z
		.string()
		.transform(val => (val === '' ? undefined : val))
		.optional(),
	customId: z
		.string()
		.transform(val => (val === '' ? undefined : val))
		.optional()
});
type SearchCodelistRecordKey = keyof z.infer<typeof searchCodelistRecordSchema>;
const searchCodelistRecordKeys: SearchCodelistRecordKey[] = Object.keys(
	searchCodelistRecordSchema.shape
) as SearchCodelistRecordKey[];
